import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AvatarService } from './services/avatar.service';
import { AuthService } from './services/auth.service';
import { AlertController, Platform, MenuController } from '@ionic/angular';
import { filter } from 'rxjs/operators';
import { NavigationEnd } from '@angular/router';
import { Auth } from '@angular/fire/auth';
import { TranslateService } from '@ngx-translate/core';
import { PwaService } from './services/pwa.service';
import { ThemeService } from './services/theme.service';

import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';

import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, RouterModule, TranslateModule]
})
export class AppComponent implements OnInit {
  public appPages = [
    // Main
    { title: 'Dashboard', url: '/home', icon: 'grid-outline', color: 'primary' },
    
    // Orders
    { title: 'Ride Requests', url: '/history', icon: 'car-sport-outline', color: 'primary' },
    { title: 'Deliveries', url: '/deliveries', icon: 'cube-outline', color: 'tertiary' },
    { title: 'Store Orders', url: '/food-orders', icon: 'cart-outline', color: 'warning' },

    // Fleet Management
    { title: 'Drivers', url: '/drivers', icon: 'people-outline', color: 'success' },
    { title: 'Financial Ledger', url: '/ledger', icon: 'cash-outline', color: 'secondary' }
  ];

  isMenuCollapsed = false;
  isMobile = false;
  menuEnabled = false;

  constructor(
    public avatar: AvatarService,
    public router: Router,
    private authService: AuthService,
    private alertController: AlertController,
    private platform: Platform,
    private menuCtrl: MenuController,
    private auth: Auth,
    private translate: TranslateService,
    public pwaService: PwaService,
    public themeService: ThemeService
  ) {
    this.isMobile = this.platform.is('mobile');
  }

  ngOnInit() {
    this.injectGetProjectButton();

    // Check initial route for menu visibility
    this.checkMenuVisibility();

    // Handle initial navigation based on auth state
    this.auth.onAuthStateChanged((user) => {
      if (!user) {
        this.router.navigate(['/login']);
      } else {
        // Check if we're at root route and redirect to home if authenticated
        if (this.router.url === '/') {
          this.router.navigate(['/home']);
        }
      }
    });

    // Handle menu visibility based on route data
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      // Get the activated route
      let route = this.router.routerState.root;
      while (route.firstChild) {
        route = route.firstChild;
      }

      // Check if menu should be enabled for this route
      this.menuEnabled = route.snapshot.data?.['menuEnabled'] ?? false;

      console.log('Route changed:', event.url, 'Menu enabled:', this.menuEnabled);

      // If we're at the root route, redirect to login
      if (this.router.url === '/') {
        this.router.navigate(['/login']);
      }
    });
  }

  async toggleMenu() {
    this.isMenuCollapsed = !this.isMenuCollapsed;
    const menus = await this.menuCtrl.getMenus();
    menus[0]?.classList.toggle('menu-collapsed', this.isMenuCollapsed);
  }

  gotoProfile() {
    this.router.navigateByUrl('/profile');
  }

  async logout() {
    const alert = await this.alertController.create({
      header: this.translate.instant('MENU.LOGOUT_CONFIRM_TITLE'),
      message: this.translate.instant('MENU.LOGOUT_CONFIRM_MESSAGE'),
      buttons: [
        {
          text: this.translate.instant('CANCEL'),
          role: 'cancel'
        },
        {
          text: this.translate.instant('MENU.LOGOUT'),
          handler: () => {
            this.performLogout();
          }
        }
      ]
    });

    await alert.present();
  }

  private async performLogout() {
    try {
      await this.authService.logout();
      // The router navigation is now handled in the AuthService
    } catch (error) {
      console.error('Logout failed:', error);
      // Handle logout error (e.g., show an error message to the user)
    }
  }

  private checkMenuVisibility() {
    // Get the activated route
    let route = this.router.routerState.root;
    while (route.firstChild) {
      route = route.firstChild;
    }

    // Check if menu should be enabled for this route
    this.menuEnabled = route.snapshot.data?.['menuEnabled'] ?? false;
    console.log('Initial route check:', this.router.url, 'Menu enabled:', this.menuEnabled);
  }

  injectGetProjectButton() {
    if (!(environment as any).defaultLogin?.projectButton || !(environment as any).projectUrl) return;

    const buttonId = 'get-full-project-btn';
    if (document.getElementById(buttonId)) return;

    const button = document.createElement('a');
    button.id = buttonId;
    button.href = (environment as any).projectUrl;
    button.target = '_blank';
    button.innerHTML = `
      <img src="https://pegasus-superapp-monorepo.vercel.app/favicon.ico" style="width: 24px; height: 24px;">
      <span>Get full project</span>
    `;

    const style = document.createElement('style');
    style.innerHTML = `
      #${buttonId} {
        position: fixed;
        bottom: 25px;
        right: 25px;
        background: linear-gradient(135deg, #FFD700, #FFA500);
        color: #000;
        padding: 12px 20px;
        border-radius: 50px;
        box-shadow: 0 10px 25px rgba(0,0,0,0.3);
        z-index: 10000;
        text-decoration: none;
        font-weight: 800;
        display: flex;
        align-items: center;
        gap: 10px;
        transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        animation: float-btn 3s ease-in-out infinite;
        border: 2px solid #fff;
        font-family: 'Inter', sans-serif;
        font-size: 14px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      #${buttonId}:hover {
        transform: scale(1.1) translateY(-5px);
        box-shadow: 0 15px 35px rgba(0,0,0,0.4);
        background: linear-gradient(135deg, #FFA500, #FFD700);
      }
      #${buttonId}:active {
        transform: scale(0.95);
      }
      @keyframes float-btn {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-10px); }
      }
      @media (max-width: 768px) {
        #${buttonId} span { display: none; }
        #${buttonId} { padding: 12px; border-radius: 50%; bottom: 20px; }
      }
    `;

    document.head.appendChild(style);
    document.body.appendChild(button);
  }
}
